import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AmbassadorLayout } from "./components/AmbassadorLayout";
import { StaffLayout } from "./components/StaffLayout";
import { PublicLayout } from "./components/PublicLayout";

import Login from "./pages/Login";
import AcceptInvite from "./pages/AcceptInvite";
import ChangePassword from "./pages/ChangePassword";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import AmbassadorHome from "./pages/AmbassadorHome";
import EventsList from "./pages/EventsList";
import { EventRoute } from "./components/EventRoute";
import CheckIn from "./pages/CheckIn";
import FieldMode from "./pages/FieldMode";
import Profile from "./pages/Profile";
import MyQr from "./pages/MyQr";
import PublicLead from "./pages/PublicLead";
import Notifications from "./pages/Notifications";
import PublicHome from "./pages/PublicHome";
import PublicEvents from "./pages/PublicEvents";
import PublicPartners from "./pages/PublicPartners";
import PublicAbout from "./pages/PublicAbout";
import StaffDashboard from "./pages/staff/StaffDashboard";
import ReviewQueue from "./pages/staff/ReviewQueue";
import StaffEvents from "./pages/staff/StaffEvents";
import StaffEventDetail from "./pages/staff/StaffEventDetail";
import AdminUsers from "./pages/staff/AdminUsers";
import { AdminTeams, AdminCampaigns, AdminPartners } from "./pages/staff/CrudPages";
import { AdminPoints, StaffReports } from "./pages/staff/AdminMisc";
import PartnerLeads from "./pages/staff/PartnerLeads";
import Analytics from "./pages/staff/Analytics";
import ParticipantDetail from "./pages/staff/ParticipantDetail";
import LeadOutbox from "./pages/staff/LeadOutbox";
import PartnerReport from "./pages/staff/PartnerReport";
import DaySummary from "./pages/staff/DaySummary";
import SystemConsole from "./pages/staff/SystemConsole";

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

/** Blocks the app until a user flagged must_change_password picks their own password. */
const PasswordGate = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user && user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
};

/** Public gateway for guests, in-app experience for members. */
const HomeGate = () => {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user === false) return <PublicLayout><PublicHome /></PublicLayout>;
  if (user.role === "ambassador") return <AmbassadorLayout><AmbassadorHome /></AmbassadorLayout>;
  return <Navigate to="/staff" replace />;
};

const EventsGate = () => {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user === false) return <PublicLayout><PublicEvents /></PublicLayout>;
  if (user.role === "ambassador") return <AmbassadorLayout><EventsList /></AmbassadorLayout>;
  return <Navigate to="/staff/events" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <PasswordGate>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/lead/:token" element={<PublicLead />} />
          <Route path="/change-password" element={
            <ProtectedRoute><ChangePassword /></ProtectedRoute>
          } />

          {/* public gateway */}
          <Route path="/" element={<HomeGate />} />
          <Route path="/events" element={<EventsGate />} />
          <Route path="/events/:id" element={<EventRoute />} />
          <Route element={<PublicLayout />}>
            <Route path="/partners" element={<PublicPartners />} />
            <Route path="/about" element={<PublicAbout />} />
          </Route>

          {/* authenticated ambassador app */}
          <Route element={<ProtectedRoute><AmbassadorLayout /></ProtectedRoute>}>
            <Route path="/checkin" element={<CheckIn />} />
            <Route path="/terrain" element={<FieldMode />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/qr" element={<MyQr />} />
            <Route path="/notifications" element={<Notifications />} />
          </Route>

          {/* staff back-office */}
          <Route element={
            <ProtectedRoute roles={["super_admin", "admin", "team_leader"]}><StaffLayout /></ProtectedRoute>
          }>
            <Route path="/staff" element={<StaffDashboard />} />
            <Route path="/staff/review" element={<ReviewQueue />} />
            <Route path="/staff/events" element={<StaffEvents />} />
            <Route path="/staff/events/:id" element={<StaffEventDetail />} />
            <Route path="/staff/events/:id/report" element={<PartnerReport />} />
            <Route path="/staff/events/:id/day-summary" element={<DaySummary />} />
            <Route path="/staff/lead-outbox" element={<LeadOutbox />} />
            <Route path="/staff/reports" element={<StaffReports />} />
            <Route path="/staff/analytics" element={<Analytics />} />
            <Route path="/staff/ambassadors/:id" element={<ParticipantDetail />} />
            <Route path="/staff/leads" element={<PartnerLeads />} />
          </Route>

          {/* admin-only back-office */}
          <Route element={
            <ProtectedRoute roles={["super_admin", "admin"]}><StaffLayout /></ProtectedRoute>
          }>
            <Route path="/staff/users" element={<AdminUsers />} />
            <Route path="/staff/teams" element={<AdminTeams />} />
            <Route path="/staff/campaigns" element={<AdminCampaigns />} />
            <Route path="/staff/partners" element={<AdminPartners />} />
            <Route path="/staff/points" element={<AdminPoints />} />
          </Route>

          {/* super-admin only */}
          <Route element={
            <ProtectedRoute roles={["super_admin"]}><StaffLayout /></ProtectedRoute>
          }>
            <Route path="/staff/system" element={<SystemConsole />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PasswordGate>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
